import { DateTime } from "luxon";
import { IntervalModel, IssueModel, IssueType } from "model";
import { Colors, Icon } from "swing-components";

import { translate } from "~utils";

type IssueRowProps = {
  issues: IssueModel[];
  date: string;
  onEditClick: () => void;
  // Whether this is the last issue row in the list
  // This is used to determine if the issue row should
  // have a bottom border
  isLast?: boolean;
};

export function IssueRow(props: IssueRowProps) {
  const { issues, date, onEditClick, isLast } = props;

  /***** Constants *****/
  const urgentIssue = issues.find((issue) => issue.issueKey === IssueType.UrgentConcern);

  // Filtering out potentially undefined issueKeys
  // to avoid use of non-null assertion
  const otherIssues = issues.filter(
    (issue) => issue.issueKey && issue.issueKey !== IssueType.UrgentConcern,
  );

  const showUrgentMessage = Boolean(urgentIssue);
  const localStart = DateTime.fromISO(date);

  /***** Render *****/
  return (
    <div
      css={{
        padding: "16px 0",
        display: "flex",
        gap: 16,
        borderBottom: isLast ? "none" : `1px solid ${Colors.slate200}`,
        alignItems: "flex-start",
      }}
    >
      <div css={{ display: "flex", gap: 16, flex: 1 }}>
        <span css={{ fontWeight: 600, flexShrink: 0 }}>{localStart.toFormat("LLL dd")}</span>

        <div>
          {urgentIssue?.issueKey && (
            <>
              <span>{IntervalModel.getIssueDisplayList(urgentIssue.issueKey)}</span>
              {showUrgentMessage && " - "}
            </>
          )}

          {showUrgentMessage && (
            <>
              <span
                css={{
                  a: {
                    fontWeight: 600,
                    textDecoration: "none",
                  },
                }}
                dangerouslySetInnerHTML={{
                  __html: translate("REPORT_ISSUE_CONFIRM_IMMEDIATE_ASSISTANCE"),
                }}
              />
              {otherIssues.length > 0 && ", "}
            </>
          )}

          {otherIssues.length > 0 && renderIssueList(otherIssues)}
        </div>
      </div>

      <div
        onClick={onEditClick}
        css={{ cursor: "pointer", marginTop: -2 }}
        aria-label="Edit issues"
        title="Edit issues"
      >
        <Icon name="Edit" color={Colors.blue500} />
      </div>
    </div>
  );
}

/***** Helper Functions *****/
function renderIssueList(issues: IssueModel[]) {
  return issues.map((issue, index) => {
    if (!issue.issueKey) return null;

    return (
      <span key={issue.issueKey}>
        {IntervalModel.getIssueDisplayList(issue.issueKey)}
        {index < issues.length - 1 ? ", " : ""}
      </span>
    );
  });
}
